import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, message } from 'antd';
import { createGiftOrder, queryAdminUsers } from '../users/userManageData';
import { getGiftablePlans, type GiftablePlan, type PlanListItem } from './packageManageData';

const CODE_PATTERN = /^[0-9]{21}$/;

export const GIFT_USER_OPTIONS = [
  { value: 'u1', label: 'VN-BeautyHub · 林晓' },
  { value: 'u2', label: 'TH-FashionLab · 陈可' },
  { value: 'u3', label: 'ID-GadgetZone · 王磊' },
  { value: 'u4', label: 'MY-HomeStyle · 赵敏' },
  { value: 'u5', label: 'SG-TechMart · 韩雪' }
];

type GiftFormValues = {
  userCode?: string;
  planCode?: string;
  durationDays?: number;
  reason?: string;
};

type GiftPackageModalProps = {
  open: boolean;
  plans?: PlanListItem[];
  /** 套餐行「赠送」时默认选中该套餐编码 */
  initialPlanCode?: string;
  onClose: () => void;
};

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `gift-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function planLabel(plan: Pick<GiftablePlan, 'planCode' | 'displayName'>) {
  const code = plan.planCode?.trim() ?? '';
  return plan.displayName?.trim() || code;
}

export function GiftPackageModal({ open, plans = [], initialPlanCode, onClose }: GiftPackageModalProps) {
  const [form] = Form.useForm<GiftFormValues>();
  const [userOptions, setUserOptions] = useState<{ value: string; label: string; email: string }[]>([]);
  const [planOptions, setPlanOptions] = useState<{ value: string; label: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const giftAttempt = useRef({ payload: '', key: '' });

  const defaultPlanCode = CODE_PATTERN.test(initialPlanCode?.trim() ?? '') ? initialPlanCode?.trim() : undefined;

  const mergedPlanOptions = useMemo(() => {
    const options = [...planOptions];
    if (defaultPlanCode && !options.some((item) => item.value === defaultPlanCode)) {
      const current = plans.find((item) => item.planCode?.trim() === defaultPlanCode);
      options.unshift({ value: defaultPlanCode, label: current ? planLabel(current) : defaultPlanCode });
    }
    return options;
  }, [defaultPlanCode, planOptions, plans]);

  useEffect(() => {
    if (!open) return;
    giftAttempt.current = { payload: '', key: '' };
    form.setFieldsValue({
      userCode: undefined,
      planCode: defaultPlanCode,
      durationDays: undefined,
      reason: undefined
    });
  }, [open, defaultPlanCode, form]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadUsers = async () => {
      const collected: { value: string; label: string; email: string }[] = [];
      for (let pageNum = 1; pageNum <= 10; pageNum += 1) {
        const res = await queryAdminUsers({ pageNum, pageSize: 100 });
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || '用户列表加载失败');
          break;
        }
        res.list.forEach((item) => {
          const userCode = item.userCode.trim();
          if (!CODE_PATTERN.test(userCode)) return;
          const email = item.email.trim();
          const name = item.displayName.trim() || email || userCode;
          collected.push({ value: userCode, label: name, email });
        });
        if (res.list.length < 100) break;
      }
      if (!cancelled) setUserOptions(collected);
    };
    const loadPlans = async () => {
      const collected: { value: string; label: string }[] = [];
      for (let pageNum = 1; pageNum <= 10; pageNum += 1) {
        const res = await getGiftablePlans({ pageNum, pageSize: 100 });
        if (cancelled) return;
        const records = res.content?.records;
        if (res.code !== 200 || !Array.isArray(records)) {
          if (res.code !== -1) message.error(res.message || '可赠送套餐加载失败');
          break;
        }
        records.forEach((item) => {
          const planCode = item.planCode?.trim() ?? '';
          if (!CODE_PATTERN.test(planCode)) return;
          collected.push({ value: planCode, label: planLabel(item) });
        });
        if (records.length < 100) break;
      }
      if (!cancelled) setPlanOptions(collected);
    };
    loadUsers().catch(() => {
      if (!cancelled) message.error('用户列表加载失败');
    });
    loadPlans().catch(() => {
      if (!cancelled) message.error('可赠送套餐加载失败');
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const submit = async () => {
    if (submitting) return;
    let values: GiftFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const userCode = values.userCode?.trim() ?? '';
    const planCode = values.planCode?.trim() ?? '';
    const durationDays = values.durationDays ?? 0;
    const reason = values.reason?.trim() ?? '';
    if (!CODE_PATTERN.test(userCode)) {
      message.warning('请选择用户');
      return;
    }
    if (!CODE_PATTERN.test(planCode)) {
      message.warning('请选择套餐');
      return;
    }
    if (!Number.isInteger(durationDays) || durationDays < 1) {
      message.warning('赠送天数须为大于 0 的整数');
      return;
    }
    if (!reason || reason.length > 500) {
      message.warning('赠送原因必填，最多 500 字');
      return;
    }
    const payload = `${userCode}|${planCode}|${durationDays}|${reason}`;
    if (giftAttempt.current.payload !== payload || !giftAttempt.current.key) {
      giftAttempt.current = { payload, key: createIdempotencyKey() };
    }
    setSubmitting(true);
    try {
      const res = await createGiftOrder({
        userCode,
        planCode,
        durationDays,
        reason,
        idempotencyKey: giftAttempt.current.key
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '赠送套餐失败');
        return;
      }
      const orderNo = res.content?.orderNo?.trim() ?? '';
      const tip = res.message || '已赠送';
      message.success(orderNo && !tip.includes(orderNo) ? `${tip}（订单号 ${orderNo}）` : tip);
      giftAttempt.current = { payload: '', key: '' };
      onClose();
    } catch {
      message.error('赠送套餐失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title='套餐赠送'
      open={open}
      onCancel={() => {
        if (!submitting) onClose();
      }}
      destroyOnClose
      centered
      width={480}
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' disabled={submitting} onClick={onClose}>
            取消
          </Button>
          <Button type='primary' className='wb-btn-primary rounded-lg' loading={submitting} onClick={submit}>
            确认
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout='horizontal'
        labelCol={{ flex: '100px' }}
        wrapperCol={{ flex: 1 }}
        labelAlign='right'
        requiredMark
        className='pt-1 admin-form-h'
      >
        <Form.Item name='userCode' label='用户' rules={[{ required: true, message: '请选择用户' }]}>
          <Select
            showSearch
            optionLabelProp='label'
            placeholder='请选择用户'
            options={userOptions}
            notFoundContent='暂无用户'
            popupMatchSelectWidth={false}
            popupClassName='admin-gift-user-popup'
            filterOption={(input, option) => {
              const keyword = input.trim().toLowerCase();
              if (!keyword) return true;
              const name = String(option?.label ?? '').toLowerCase();
              const email = String(option?.email ?? '').toLowerCase();
              return name.includes(keyword) || email.includes(keyword);
            }}
            optionRender={(option) => (
              <div className='admin-gift-user-option'>
                <span className='admin-gift-user-name'>{option.label}</span>
                <span className='admin-gift-user-email'>{option.data.email}</span>
              </div>
            )}
          />
        </Form.Item>
        <Form.Item name='planCode' label='套餐' rules={[{ required: true, message: '请选择套餐' }]}>
          <Select
            showSearch
            optionFilterProp='label'
            placeholder='请选择套餐'
            options={mergedPlanOptions}
            notFoundContent='暂无套餐'
          />
        </Form.Item>
        <Form.Item
          name='durationDays'
          label='有效天数'
          rules={[{ required: true, message: '请输入赠送天数' }]}
        >
          <InputNumber className='w-full' min={1} precision={0} placeholder='请输入赠送天数' style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='reason' label='赠送原因' rules={[{ required: true, message: '请输入赠送原因' }]}>
          <Input.TextArea rows={4} placeholder='请输入赠送原因' maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
